import { afterNextRender,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnInit,
  ViewChild } from '@angular/core';
import moment from 'moment';
import { TranslateModule } from '@ngx-translate/core';

import { MonthsAbrvLabels, MonthsLabels } from './mp-calendar.model';
import { Moment } from 'moment/moment';
import { FormsModule } from '@angular/forms';
import { MpHourPickerComponent } from '../../mp-hour-picker/mp-hour-picker.component';
import { MpToggleComponent } from '../../mp-toggle/mp-toggle.component';
import { MpButtonComponent } from '../../mp-button/mp-button.component';
import { DateType, FixedDate, IntervalDate } from '../../../models/date.model';
import { FixedTime, IntervalTime, TimeType } from '../../../models/time.model';

@Component({
  selector: 'app-mp-calendar',
  templateUrl: './mp-calendar.component.html',
  imports: [TranslateModule, FormsModule, MpHourPickerComponent, MpToggleComponent, MpButtonComponent],
  styleUrl: './mp-calendar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MpCalendarComponent implements OnInit {

  @ViewChild('calendar', { read: ElementRef }) calendarElementRef: ElementRef;

  @Input({ required: true }) anchor: ElementRef;

  @Input() public startDay: number;
  @Input() public startMonth: number;
  @Input() public startYear: number;
  @Input() public endDay: number;
  @Input() public endMonth: number;
  @Input() public endYear: number;

  @Input() public startTime: string;
  @Input() public endTime: string;

  @Input() public minDate: Date;
  @Input() public onlyBusinessDays: boolean = true;

  @Input() public timeIntervalAllowed: boolean = false;
  @Input() public dateIntervalAllowed: boolean = false;

  @Input() public datePicked: (dateValue: FixedDate | IntervalDate, time: FixedTime | IntervalTime) => {};
  @Input() public resetSelection: () => {};
  @Input() public resetStartTime: () => {};
  @Input() public resetEndTime: () => {};

  public viewedYear: number;
  public viewedMonth: number;
  public daysInMonth: number;
  public days: number[];
  public years: number[];
  public months: number[] = Array.from({ length: 12 }, (value, index) => index);
  public daysPadding: number[];

  public yearsPage: number = 0;

  public startDate: Moment;
  public endDate: Moment;
  public startToggle: boolean = true;
  public endToggle: boolean = true;

  public currentView: 'DAY' | 'MONTH' | 'YEAR' = 'DAY';

  protected readonly MonthsLabels = MonthsLabels;
  protected readonly MonthsAbrvLabels = MonthsAbrvLabels;

  private confirmSelectionTimeout: ReturnType<typeof setTimeout>;

  constructor(private readonly elementRef: ElementRef,
              private readonly ngZone: NgZone) {
    afterNextRender({
      write: () => {
        this.computePosition();
        this.scrollToCalendar();
      }
    });
  }

  ngOnInit() {
    this.initView();
    if (this.startYear &&
        (this.startMonth !== undefined && this.startMonth !== null) &&
        this.startDay) {
      this.startDate = moment().year(this.startYear).month(this.startMonth).date(this.startDay);
    }
    if (this.endYear &&
        (this.endMonth !== undefined && this.endMonth !== null) &&
        this.endDay) {
      this.endDate = moment().year(this.endYear).month(this.endMonth).date(this.endDay);
    }
  }

  public isStart(day: number) {
    const dayDate: Moment = moment().year(this.viewedYear).month(this.viewedMonth).date(day);
    return this.startDate && this.startDate.isSame(dayDate, 'day');
  }

  public isEnd(day: number) {
    const dayDate: Moment = moment().year(this.viewedYear).month(this.viewedMonth).date(day);
    return this.endDate && this.endDate.isSame(dayDate, 'day');
  }

  public dayIsBetween(day: number) {
    const dayDate: Moment = moment().year(this.viewedYear).month(this.viewedMonth).date(day);
    return this.startDate && this.endDate && dayDate.isBetween(this.startDate, this.endDate);
  }

  protected isBusinessDay(day: number): boolean {
    const dayDate: number = moment().year(this.viewedYear).month(this.viewedMonth).date(day).day();
    return this.isBusinessDay && dayDate !== 0 && dayDate !== 6;
  }

  public previousPeriod() {

    if (!this.canGoToPreviousPeriod()) {
      return;
    }

    if (this.currentView === 'YEAR') {
      this.yearsPage = this.yearsPage - 1;
      this.computeYearsList();
    } else if (this.currentView === 'DAY') {
      if (this.viewedMonth === 0) {
        this.viewedMonth = 11;
        this.viewedYear = this.viewedYear - 1;
      } else {
        this.viewedMonth = this.viewedMonth - 1;
      }
      this.computeDaysList();
    }
  }

  public nextPeriod() {
    if (this.currentView === 'YEAR') {
      this.yearsPage = this.yearsPage + 1;
      this.computeYearsList();
    } else if (this.currentView === 'DAY') {
      if (this.viewedMonth === 11) {
        this.viewedMonth = 0;
        this.viewedYear = this.viewedYear + 1;
      } else {
        this.viewedMonth = this.viewedMonth + 1;
      }
      this.computeDaysList();
    }
  }

  public selectDay(day: number) {
    this.startDay = day;
    const newDate = moment().year(this.viewedYear).month(this.viewedMonth).date(day);

    // Case 1: Single date selection mode or starting a new interval
    if (!this.startDate || !this.dateIntervalAllowed) {
      this.startDate = newDate;
      // Clear endDate in single date selection mode or reset interval
      this.endDate = undefined;
      return;
    }

    // Case 2: Interval selection mode is allowed
    // Reset interval if both dates are set, or if newDate is before or equal to startDate
    if (this.endDate || newDate.isSameOrBefore(this.startDate, 'day')) {
      this.startDate = newDate;
      // Reset endDate
      this.endDate = undefined;
    }
    // Extend the interval if newDate is after startDate
    else if (newDate.isAfter(this.startDate, 'day')) {
      this.endDate = newDate;
    }
  }

  public selectMonth(event: MouseEvent, month: number) {
    event.stopPropagation();
    this.viewedMonth = month;
    this.showDayList();
  }

  public selectYear(event: MouseEvent, year: number) {
    event.stopPropagation();
    this.viewedYear = year;
    this.currentView = 'MONTH';
  }

  public showYearList(event: MouseEvent) {
    event.stopPropagation();
    this.yearsPage = 0;
    this.computeYearsList();
    this.currentView = 'YEAR';
  }

  public showDayList(event?: MouseEvent) {
    if (event) {
      event.stopPropagation();
    }
    this.computeDaysList();
    this.currentView = 'DAY';
  }

  public confirmSelection() {
    if (this.confirmSelectionTimeout) {
      clearTimeout(this.confirmSelectionTimeout);
    }

    this.confirmSelectionTimeout = setTimeout(() => {
      const pickedDate = this.confirmDateSelection();
      const pickedTime = this.confirmTimeSelection();
      this.datePicked(pickedDate, pickedTime);
    }, 300);
  }


  public resetTimeField(field: 'start' | 'end') {
    if (field === 'start') {
      this.startTime = '';
      this.resetStartTime();
    } else if (field === 'end') {
      this.endTime = '';
      this.resetEndTime();
    }
  }

  public resetCalendar() {
    this.startDate = undefined;
    this.endDate = undefined;
    this.startTime = '';
    this.endTime = '';
    this.resetSelection();
  }

  protected isBeforeMinDate(day: number): boolean {
    if (!this.minDate) {
      return false;
    }
    const currentDate = moment().year(this.viewedYear).month(this.viewedMonth).date(day).startOf('day');
    return currentDate.isBefore(moment(this.minDate).startOf('day'));
  }

  protected isMonthBeforeMinDate(year: number, month: number): boolean {
    if (!this.minDate) {
      return false;
    }
    return moment().year(year).month(month).endOf('month').isBefore(moment(this.minDate));
  }

  public isYearBeforeMinDate(year: number): boolean {
    if (!this.minDate) {
      return false;
    }
    return moment().year(year).endOf('year').isBefore(moment(this.minDate));
  }

  private initView() {
    const referenceDate = moment();
    if (this.startYear) {
      this.viewedYear = this.startYear;
      referenceDate.year(this.viewedYear);
    } else {
      this.viewedYear = referenceDate.year();
    }

    if (this.startMonth || this.startMonth === 0) {
      this.viewedMonth = this.startMonth;
      referenceDate.month(this.viewedMonth);
    } else {
      this.viewedMonth = referenceDate.month();
    }

    this.computeDaysList();
  }

  protected canGoToPreviousPeriod(): boolean {
    if (!this.minDate) {
      return true;
    }

    const min = moment(this.minDate);

    if (this.currentView === 'DAY') {
      const previous = moment().year(this.viewedYear).month(this.viewedMonth).date(1).subtract(1, 'month').endOf('month');
      return previous.isSameOrAfter(min, 'day');
    }

    if (this.currentView === 'YEAR') {
      const earliestYear = this.years[0] - 1;
      // Going to previous year page
      const previous = moment().year(earliestYear).endOf('year');
      return previous.isSameOrAfter(min, 'day');
    }

    return true;
  }

  private confirmDateSelection() {
    let pickedDate;

    if (this.endDate) {
      pickedDate = {
        type: DateType.INTERVAL_DATE,
        start: this.startDate ? this.startDate.toDate() : undefined,
        end: this.endDate ? this.endDate.toDate() : undefined
      };
    } else {
      pickedDate = {
        type: DateType.FIXED_DATE,
        date: this.startDate?.toDate()
      };
    }

    return pickedDate;
  }

  private confirmTimeSelection() {
    let pickedTime;

    if (this.startTime && this.endTime) {
      pickedTime = {
        type: TimeType.INTERVAL_TIME,
        start: this.startTime,
        end: this.endTime
      };
    } else if (this.startTime || this.endTime) {
      pickedTime = {
        type: TimeType.FIXED_TIME,
        time: this.startTime ? this.startTime : this.endTime
      };
    }

    return pickedTime;
  }

  private computeDaysList() {
    const referenceDate = moment().month(this.viewedMonth).year(this.viewedYear);
    this.daysInMonth = referenceDate.daysInMonth();
    this.days = Array.from({ length: this.daysInMonth }, (value, index) => index + 1);
    const firstDayOfTheMonth = referenceDate.startOf('month').isoWeekday();
    this.daysPadding = Array.from({ length: firstDayOfTheMonth - 1 }, (value, index) => index + 1);
  }

  private computeYearsList() {
    const offset = this.viewedYear % 24;
    this.years = Array.from({ length: 24 }, (value, index) => this.viewedYear - offset + index + (this.yearsPage * 24));
  }

  private computePosition() {
    this.ngZone.runOutsideAngular(() => {
      const { bottom, height } = this.anchor.nativeElement.getBoundingClientRect();
      const maxHeight = window.innerHeight;
      const dropDownMeasure = this.calendarElementRef?.nativeElement?.getBoundingClientRect();

      let translateY: number;

      if (bottom + dropDownMeasure.height > maxHeight - 10) {
        translateY = -dropDownMeasure.height - height + 1;
      } else {
        translateY = -1;
      }

      this.calendarElementRef.nativeElement.style.transform = `translateY(${translateY}px)`;
    });
  }

  private scrollToCalendar(): void {
    this.calendarElementRef?.nativeElement?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }
}
